import { DateTime } from "luxon"

import { ProductFields } from "@tenforce/product-fields"

import { dataAPI } from "./api"
import {
    parseOdataDateValue,
    getFormatBasedOnType,
} from "./utils/dateTimeParsing"
import {
    parseFieldData,
    parseOptionField,
    parseItemAction,
    parseLocationCoordinates,
} from "./utils/fieldparsing"
import { DescriptionComponentCode, StatusCode } from "./utils/constants"
import { RequestErrorDataApi } from "../errors"
import env from "../env"
import { ITabComponent } from "../components/itemdetail"
import {
    BasicOptionDTO,
    FieldDTO,
    FlowControlDTO,
    ItemActionDTO,
    ItemViewComponentDTO,
    ItemViewGroupingComponentDTO,
    LocationFieldDTO,
    OptionItemDTO,
    SelectableValues,
    SelectableValuesGeneric,
    SingleFieldComponentDTO,
    MultiFieldsComponentDTO,
} from "./models/fields"
import { ODataGetArrayResponse } from "./models/api/data"
import {
    IField,
    IItemPickerField,
    ILocationField,
    ISelectorField,
    IToggleButtonField,
} from "../components/fields"
import { IUserFormattingSettings } from "../components/applicationheader/components/usermenu"
import { IButton } from "../components/common/contextmenu"
import { parseItemPickerData, selectorToSingularTypeMapper } from "./selectors"
import { ItemDTO } from "./models/selectors/models"

export const getFields = (
    itemId: number,
    userFormattingSettings: IUserFormattingSettings,
): Promise<IField[]> =>
    dataAPI
        .getJSON<ItemViewGroupingComponentDTO>(
            `itemviews(itemid=${itemId},code='itemdetail')`,
        )
        .then((itemDetail) => {
            return extractFields(itemDetail, userFormattingSettings)
        })

export const updateFieldValue = async (
    itemId: number,
    field: IField,
): Promise<IField> => {
    const { fieldId, value } = field

    const args = {
        "@type": field.odataType,
        Value: value,
    }

    if (field.type === "date") {
        const newDate = field.value
            ? DateTime.fromISO(field.value as string)
            : null

        args.Value =
            newDate?.toFormat(getFormatBasedOnType(field.odataType)) ?? null
    }

    if (field.type === "itemPicker") {
        const parsedField = field as IItemPickerField
        const parsedValue = value as number[]
        if (parsedField.isSingleSelect) {
            if (parsedValue.length) {
                args.Value = {
                    "@type":
                        selectorToSingularTypeMapper[parsedField.odataType],
                    id: parsedValue[0],
                }
            } else {
                args.Value = null
            }
        } else {
            args.Value = parsedValue.map((valueId) => ({
                "@type": selectorToSingularTypeMapper[parsedField.odataType],
                id: valueId,
            }))
        }
    }

    if (field.type === "location") {
        const value = (field as ILocationField).value
        args.Value = {
            Point: {
                type: "Point",
                coordinates: [value?.longitude, value?.latitude],
            },
        }
    }

    if (field.type === "time") {
        let newValue = null
        if (value) {
            newValue = DateTime.fromISO(value as string).toFormat(
                getFormatBasedOnType(field.odataType),
            )
        }

        args.Value = newValue
    }

    if (field.type === "selector") {
        const parsedField = field as ISelectorField
        const parsedValue = value as number[]
        if (parsedField.isSingleSelect) {
            if (parsedValue.length) {
                args.Value = {
                    "@type":
                        selectorToSingularTypeMapper[parsedField.odataType],
                    id: parsedValue[0],
                }
            } else {
                args.Value = null
            }
        } else {
            args.Value = parsedValue.map((valueId) => ({
                "@type": selectorToSingularTypeMapper[parsedField.odataType],
                id: valueId,
            }))
        }
    }

    if (field.type === "toggle") {
        const value = field.value as string
        if (value) {
            args.Value = {
                Id: parseInt(value),
            }
        } else {
            args.Value = null
        }
    }

    try {
        const fieldDTO = await dataAPI.patchJSON<FieldDTO>(
            `itemfields(ItemId=${itemId},FieldDefinitionId='${fieldId}')`,
            args,
        )

        if (field.type === "date") {
            fieldDTO.Value = parseOdataDateValue(
                (fieldDTO.Value as string) ?? "",
                getFormatBasedOnType(fieldDTO["@type"]),
            )
        } else if (field.type === "itemPicker") {
            fieldDTO.Value =
                fieldDTO.Value === null
                    ? []
                    : parseItemPickerData(fieldDTO.Value as ItemDTO)
        } else if (field.type === "location") {
            const { MapMarker } = fieldDTO as LocationFieldDTO
            const coordinates = parseLocationCoordinates(
                fieldDTO.Value as LocationFieldDTO["Value"],
            )
            fieldDTO.Value = {
                ...coordinates,
                marker: {
                    id: MapMarker.Id,
                    imageSrc: `${env.applicationPath}/dataapi/MapMarkers(${MapMarker.Id})/$value`,
                    anchor: [MapMarker.AnchorX, MapMarker.AnchorY],
                    size: [MapMarker.ImageWidth, MapMarker.ImageHeight],
                },
            }
        } else if (field.type === "time") {
            fieldDTO.Value = parseOdataDateValue(
                (fieldDTO.Value as string) ?? "",
                getFormatBasedOnType(fieldDTO["@type"]),
            )
        } else if (field.type === "toggle") {
            fieldDTO.Value = fieldDTO.Value
                ? parseOptionField(fieldDTO.Value as OptionItemDTO, true)
                : null
        }

        return {
            ...field,
            value: fieldDTO.Value,
        }
    } catch (error) {
        console.error(
            `An error occurred while updating the field ${field.title} with value ${field.value}: `,
            error,
        )
        const errorMessage = error instanceof Error ? error.message : ""
        if (error instanceof Response) {
            throw await RequestErrorDataApi.create(
                errorMessage,
                error.status,
                error,
            )
        }
        throw error // Rethrow the error if you need to handle it further up the chain
    }
}

export const collectSelectableValuesGeneric = <
    ApiOptionType extends BasicOptionDTO,
    ResultType,
>(
    itemId: number,
    fieldId: string,
    fieldName: string,
    conversionFunction: (value: ApiOptionType) => ResultType,
    queryOptions?: string,
): Promise<ResultType[]> =>
    dataAPI
        .getJSON<SelectableValuesGeneric<ApiOptionType>>(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/TF.${fieldName}/SelectableValues${queryOptions}`,
        )
        .then((selectables) => {
            const parsedSelectables = selectables.value
                .filter((selectable) => selectable.IsActive)
                .map((selectable: ApiOptionType) =>
                    conversionFunction(selectable),
                )

            return parsedSelectables
        })

export const collectSelectableValues = (
    itemId: number,
    field: IField,
): Promise<ProductFields.ToggleItem[]> =>
    dataAPI
        .getJSON<SelectableValues>(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${field.fieldId}'/TF.OptionItemField/SelectableValues`,
        )
        .then((selectables) => {
            const toggleField = field as IToggleButtonField
            const selectedFieldId = toggleField.value
                ? toggleField.value.valueId
                : "-1"

            const parsedSelectables = selectables.value.map(
                (selectable: OptionItemDTO) =>
                    parseOptionField(
                        selectable,
                        selectedFieldId === selectable.Id.toString(),
                    ),
            )

            return parsedSelectables
        })

export const collectStateTransitionButtons = (
    itemId: number,
): Promise<FlowControlDTO[]> =>
    collectSelectableValuesGeneric(
        itemId,
        StatusCode,
        "StatusItemField",
        (value) => value as FlowControlDTO,
        "",
    )
        .catch((reason) => {
            if (reason.status === 404) {
                return [] as FlowControlDTO[]
            } else {
                throw reason
            }
        })
        .then((buttons) =>
            buttons.filter(
                // Only a FlowControl will have a "Type" property,
                // this will filter out normal statuses and hidden flow controls
                (button) => !!button.Type && button.Type !== "Hidden",
            ),
        )

export const collectItemActions = (itemId: number): Promise<IButton[]> =>
    dataAPI
        .getJSON<ODataGetArrayResponse<ItemActionDTO>>(
            `ItemViews(ItemId=${itemId},Code='condensed')/Actions?$filter=Action/ApplicationPart has TF.ApplicationPart'Web'&$orderby=SequenceNr`,
        )
        .then((itemActions) => {
            return itemActions.value.map((itemAction) =>
                parseItemAction(itemAction),
            )
        })

const extractComponents = (
    value: ItemViewComponentDTO | ItemViewGroupingComponentDTO,
): (ItemViewComponentDTO | ItemViewGroupingComponentDTO)[] => {
    const array = []
    if (value) {
        array.push(value)
    }

    if ("Components" in value) {
        array.push(
            ...value.Components.flatMap((component) =>
                extractComponents(component),
            ),
        )
    }
    return array
}

export const extractFields = (
    itemDetail: ItemViewGroupingComponentDTO,
    userFormattingSettings: IUserFormattingSettings,
): IField[] => {
    const components = extractComponents(itemDetail)
    const descriptionComponents = components.filter(
        (component) => component.ComponentCode === DescriptionComponentCode,
    )
    const sectionComponent = descriptionComponents[0]
    const textComponent = descriptionComponents[1]
    if (
        textComponent?.ComponentDefinition &&
        sectionComponent.ComponentDefinition
    ) {
        textComponent.ComponentDefinition.Label =
            sectionComponent.ComponentDefinition.Label
    }

    return components.map((field) =>
        parseFieldData(
            field as
                | SingleFieldComponentDTO
                | MultiFieldsComponentDTO
                | ItemViewGroupingComponentDTO,
            userFormattingSettings,
        ),
    )
}

export const parseTabs = (
    itemDetail: ItemViewGroupingComponentDTO,
    userFormattingSettings: IUserFormattingSettings,
): ITabComponent[] => {
    const description = itemDetail.MetaData.filter(
        (component) => component.ComponentCode === DescriptionComponentCode,
    ) as ItemViewComponentDTO[]
    const tabs = itemDetail.Components.map(
        (viewComponent: ItemViewGroupingComponentDTO, index) => {
            const components = viewComponent.Components
            if (index == 0) {
                components.concat(description)
            }

            return {
                label: viewComponent.ComponentDefinition.Label,
                code: viewComponent.ComponentCode,
                fields: components.map((field) =>
                    parseFieldData(
                        field as
                            | SingleFieldComponentDTO
                            | MultiFieldsComponentDTO,
                        userFormattingSettings,
                    ),
                ),
            } as ITabComponent
        },
    )

    return tabs
}
